import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  LinearProgress, 
  List, 
  ListItem, 
  ListItemText, 
  Chip,
  Divider,
  useTheme
} from '@mui/material';
import { ClientCompletenessScore } from '../../../types/utility.types';

interface ClientCompletenessProps {
  data: ClientCompletenessScore[];
  isLoading: boolean;
}

const ClientCompleteness: React.FC<ClientCompletenessProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Client Data Completeness
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Format field names for display
  const formatFieldName = (field: string): string => {
    switch (field) {
      case 'companyName':
        return 'Company Name';
      case 'ownerName':
        return 'Owner Name';
      case 'email':
        return 'Email';
      case 'phoneNumber':
        return 'Phone Number';
      case 'tradingAddress':
        return 'Trading Address';
      case 'businessType':
        return 'Business Type';
      case 'industry':
        return 'Industry';
      default:
        return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    }
  };

  // Get color based on completeness score
  const getScoreColor = (score: number): string => {
    if (score < 50) return theme.palette.error.main;
    if (score < 80) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Client Data Completeness
        </Typography>
        {data.length > 0 ? (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {data.map((client, index) => (
              <React.Fragment key={client.clientId}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1">
                          {client.name || 'Unnamed Client'}
                        </Typography>
                        <Chip 
                          label={`${client.completenessScore}%`} 
                          size="small"
                          sx={{ 
                            backgroundColor: getScoreColor(client.completenessScore),
                            color: '#fff'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <LinearProgress 
                          variant="determinate" 
                          value={client.completenessScore} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 5,
                            mb: 1,
                            backgroundColor: theme.palette.grey[300],
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(client.completenessScore)
                            }
                          }}
                        />
                        {client.missingFields.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Missing information:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {client.missingFields.map((field) => (
                                <Chip 
                                  key={field} 
                                  label={formatFieldName(field)} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
                {index < data.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientCompleteness;
